import React, { useState } from 'react';
import { PolicyType } from '../../types';
import { apiService } from '../../services/api';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

interface TriggerScrapeButtonProps {
  companyId: string;
  policyType?: PolicyType;
  compact?: boolean;
  onSuccess?: (jobResult: any) => void;
}

export const TriggerScrapeButton: React.FC<TriggerScrapeButtonProps> = ({
  companyId,
  policyType = 'terms_of_service',
  compact = false,
  onSuccess,
}) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleTrigger = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setStatus('loading');
    setErrorMessage(null);

    try {
      const result = await apiService.triggerScrape(companyId, policyType);
      setStatus('success');
      onSuccess?.(result);
      setTimeout(() => setStatus('idle'), 3000);
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || 'Scrape trigger failed');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleTrigger}
        disabled={status === 'loading'}
        className={`p-1.5 rounded-md text-xs transition-colors flex items-center gap-1 border border-slate-200 dark:border-slate-700 ${
          status === 'loading'
            ? 'bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
            : status === 'success'
            ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
            : status === 'error'
            ? 'bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400'
            : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300'
        }`}
        title={errorMessage || 'Trigger live scrape and AI analysis'}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
        {status === 'loading' && <span className="text-[10px]">Scraping...</span>}
        {status === 'success' && <CheckCircle2 className="w-3.5 h-3.5" />}
        {status === 'error' && <AlertCircle className="w-3.5 h-3.5" />}
      </button>
    );
  }

  return (
    <button
      onClick={handleTrigger}
      disabled={status === 'loading'}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all shadow-sm ${
        status === 'loading'
          ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300 cursor-wait'
          : status === 'success'
          ? 'bg-emerald-600 text-white'
          : status === 'error'
          ? 'bg-rose-600 text-white'
          : 'bg-indigo-600 text-white hover:bg-indigo-700'
      }`}
    >
      <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
      {status === 'loading'
        ? 'Scraping & Analyzing...'
        : status === 'success'
        ? 'Ingestion Complete!'
        : status === 'error'
        ? 'Scrape Failed'
        : 'Trigger Live Scrape'}
    </button>
  );
};
